// PlotsPage.js

import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { useDebounce } from "use-debounce";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  DollarSign,
  User,
  Grid,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  TrendingUp,
  TrendingDown,
  Building,
  Calendar,
} from "lucide-react";

const PlotsPage = () => {
  const { projectName } = useParams();

  // entire dataset
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [statusFilter, setStatusFilter] = useState("all");
  const [varianceFilter, setVarianceFilter] = useState("all");

  // pagination
  const [page, setPage] = useState(1);
  const perPage = 20;

  // sorting
  const [sortConfig, setSortConfig] = useState({
    key: "Plot_NO",
    direction: "asc",
  });

  // helper to extract sortable value (handles variance)
  const getValue = (row, key) => {
    if (key === "variance") {
      return (row.totalPaid || 0) - (row.purchase_price || 0);
    }
    return row[key] ?? "";
  };

  // 1) Fetch _all_ plots so client-side can sort/filter/paginate
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get(
          `https://workspace.optiven.co.ke/api/mib/projects/${encodeURIComponent(
            projectName
          )}/plots/paginated?page=1&limit=100000`
        );
        setPlots(Array.isArray(data.plots) ? data.plots : []);
      } catch (err) {
        console.error(err);
        setError("Failed to load plots. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [projectName]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(
        `https://workspace.optiven.co.ke/api/mib/projects/${encodeURIComponent(
          projectName
        )}/plots/paginated?page=1&limit=100000`
      );
      setPlots(Array.isArray(data.plots) ? data.plots : []);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  // reset to first page on search or sort change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, sortConfig]);

  // 2) Filter by search and other criteria
  const filtered = useMemo(() => {
    let filteredPlots = [...plots];

    // Search filter
    if (debouncedSearchTerm) {
      const term = debouncedSearchTerm.toLowerCase();
      filteredPlots = filteredPlots.filter(
        (p) =>
          (p.Plot_NO || "").toLowerCase().includes(term) ||
          (p.Customer_Name || "").toLowerCase().includes(term)
      );
    }

    // Status filter (if we have status data)
    if (statusFilter !== "all") {
      filteredPlots = filteredPlots.filter((p) => {
        // This would need to be adapted based on actual status field
        return p.status === statusFilter;
      });
    }

    // Variance filter
    if (varianceFilter !== "all") {
      filteredPlots = filteredPlots.filter((p) => {
        const price = p.purchase_price || 0;
        const paid = p.totalPaid || 0;
        const diff = paid - price;

        switch (varianceFilter) {
          case "overpaid":
            return diff > 0;
          case "underpaid":
            return diff < 0;
          case "exact":
            return diff === 0;
          default:
            return true;
        }
      });
    }

    return filteredPlots;
  }, [plots, debouncedSearchTerm, statusFilter, varianceFilter]);

  // 3) Sort, including variance
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, direction } = sortConfig;
    arr.sort((a, b) => {
      const aVal = getValue(a, key);
      const bVal = getValue(b, key);

      // numeric compare if both are numbers
      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      // fallback to string compare
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr > bStr) return direction === "asc" ? 1 : -1;
      if (aStr < bStr) return direction === "asc" ? -1 : 1;
      return 0;
    });
    return arr;
  }, [filtered, sortConfig]);

  // 4) Paginate
  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const currentData = useMemo(() => {
    const start = (page - 1) * perPage;
    return sorted.slice(start, start + perPage);
  }, [sorted, page]);

  // toggle sort on any column
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const getIndicator = (key) =>
    sortConfig.key === key ? (sortConfig.direction === "asc" ? "▲" : "▼") : "";

  // loading / error
  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-lg font-medium text-gray-700">
              Loading plots...
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Please wait while we fetch the latest data
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Error Loading Plots
            </h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  // UI render
  return (
    <Sidebar>
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Plots for {projectName}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and review plot information and payments
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Plots</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {plots.length}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Filtered</p>
                  <p className="text-2xl font-bold text-green-600">
                    {filtered.length}
                  </p>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${
                      refreshing ? "animate-spin" : ""
                    }`}
                  />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search by plot number or customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={varianceFilter}
                  onChange={(e) => setVarianceFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Variances</option>
                  <option value="overpaid">Overpaid</option>
                  <option value="underpaid">Underpaid</option>
                  <option value="exact">Exact Match</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      {
                        key: "Plot_NO",
                        label: "Plot Details",
                        align: "left",
                        icon: Grid,
                      },
                      {
                        key: "Customer_Name",
                        label: "Customer",
                        align: "left",
                        icon: User,
                      },
                      {
                        key: "purchase_price",
                        label: "Purchase Price",
                        align: "right",
                        icon: DollarSign,
                      },
                      {
                        key: "totalPaid",
                        label: "Total Paid",
                        align: "right",
                        icon: DollarSign,
                      },
                      {
                        key: "variance",
                        label: "Variance",
                        align: "right",
                        icon: TrendingUp,
                      },
                    ].map(({ key, label, align, icon: Icon }) => (
                      <th
                        key={key}
                        onClick={() => requestSort(key)}
                        className={`px-6 py-3 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                      >
                        <div className="flex items-center space-x-2">
                          {Icon && <Icon className="h-4 w-4" />}
                          <span>{label}</span>
                          {sortConfig.key === key && (
                            <span>
                              {sortConfig.direction === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentData.map((row) => {
                    const price = row.purchase_price || 0;
                    const paid = row.totalPaid || 0;
                    const diff = paid - price;

                    let badge;
                    if (diff > 0) {
                      badge = (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <TrendingUp className="h-3 w-3 mr-1" />+ Ksh{" "}
                          {diff.toLocaleString()}
                        </span>
                      );
                    } else if (diff < 0) {
                      badge = (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <TrendingDown className="h-3 w-3 mr-1" />− Ksh{" "}
                          {Math.abs(diff).toLocaleString()}
                        </span>
                      );
                    } else {
                      badge = (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Exact
                        </span>
                      );
                    }

                    return (
                      <tr key={row.Plot_NO} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <Grid className="h-4 w-4 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {row.Plot_NO}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {row.Customer_Name || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {price ? `Ksh ${price.toLocaleString()}` : "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {paid ? `Ksh ${paid.toLocaleString()}` : "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          {badge}
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap text-sm font-medium">
                          <Link
                            to={`/projects/${encodeURIComponent(
                              projectName
                            )}/plots/${encodeURIComponent(row.Plot_NO)}`}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View Details
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                  {currentData.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <Grid className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                          No plots found
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {searchTerm
                            ? "No plots match your search criteria."
                            : "No plots available."}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    disabled={page === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {(page - 1) * perPage + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(page * perPage, sorted.length)}
                      </span>{" "}
                      of <span className="font-medium">{sorted.length}</span>{" "}
                      results
                    </p>
                  </div>
                  <div>
                    <nav
                      className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => setPage((p) => p - 1)}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronDown className="h-5 w-5 transform rotate-90" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageNum === page
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {pageNum}
                          </button>
                        )
                      )}
                      <button
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronDown className="h-5 w-5 transform -rotate-90" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default PlotsPage;
